import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, ChevronsLeft, Trophy, ShoppingCart, Trash2, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import marketingService from '../services/marketingService';
import TreeStructure from './TreeStructure';

const LightBoxCluster = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isActivated, setIsActivated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [availableTariffs, setAvailableTariffs] = useState([]);
    const [activeTariff, setActiveTariff] = useState('start');
    const [activeLevel, setActiveLevel] = useState(null);
    const [basketItems, setBasketItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [activationDetails, setActivationDetails] = useState(null);

    const [showTree, setShowTree] = useState(false);
    const [treeParams, setTreeParams] = useState(null);
    const [queueInfo, setQueueInfo] = useState(null);

    const [basketTimer, setBasketTimer] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [canPay, setCanPay] = useState(false);
    const [hasCheckedStatus, setHasCheckedStatus] = useState(false);

    // STATE для обработки платежей
    const [basketStatus, setBasketStatus] = useState('basket');
    const [processingInfo, setProcessingInfo] = useState(null);
    const [isPaymentClicked, setIsPaymentClicked] = useState(false);
    const [lastBasketItems, setLastBasketItems] = useState([]);
    const [serverTimeOffset, setServerTimeOffset] = useState(0);

    const programParam = searchParams.get('program');
    const levelParam = searchParams.get('level');
    const mentorParam = searchParams.get('mentor');

    useEffect(() => {
        loadOptions();
        loadBasket();
    }, []);

    useEffect(() => {
        if (availableTariffs.length > 0 && !hasCheckedStatus) {
            setHasCheckedStatus(true);
            checkActivationStatus();
        }
    }, [availableTariffs.length, hasCheckedStatus]);

    // Проверка статуса каждые 10 секунд если payment_processing или in_queue
    useEffect(() => {
        if (basketStatus === 'payment_processing' || basketStatus === 'in_queue') {
            const interval = setInterval(() => {
                checkActivationStatus();
            }, 10000);
            return () => clearInterval(interval);
        }
    }, [basketStatus]);

    useEffect(() => {
        if (programParam && levelParam && mentorParam !== null) {
            console.log('Loaded with params:', { programParam, levelParam, mentorParam });
        }
    }, [programParam, levelParam, mentorParam]);

    useEffect(() => {
        if (!basketTimer) {
            setCanPay(true);
            setTimeRemaining(0);
            return;
        }

        const targetTime = new Date(basketTimer).getTime();

        const updateTimer = () => {
            const now = new Date().getTime();
            const distance = targetTime - now;

            if (distance <= 0) {
                setTimeRemaining(0);
                setCanPay(true);
                return true;
            }

            setTimeRemaining(distance);
            setCanPay(false);
            return false;
        };

        const shouldStop = updateTimer();
        if (shouldStop) return;

        const interval = setInterval(() => {
            const shouldStop = updateTimer();
            if (shouldStop) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [basketTimer]);

    const formatTime = (milliseconds) => {
        const totalSeconds = Math.floor(milliseconds / 1000);

        if (totalSeconds <= 0) return "00:00:00";

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const checkActivationStatus = async () => {
        try {
            const response = await marketingService.checkActivation();
            if (response.success) {
                const hasActivation = response.data.is_activated;

                if (hasActivation) {
                    setIsActivated(true);
                    setBasketStatus('basket');
                    setProcessingInfo(null);
                    setActivationDetails(response.data.activation_details);

                    if (response.data.activation_details) {
                        setShowTree(true);
                        setTreeParams({
                            tarif: response.data.activation_details.tarif,
                            level: response.data.activation_details.level,
                            numberLevel: 1
                        });
                    }
                    return;
                }

                // Проверяем статус первого уровня
                if (availableTariffs.length > 0) {
                    const firstTariff = availableTariffs[0];

                    if (firstTariff.levels.length > 0) {
                        const firstLevel = firstTariff.levels[0];

                        try {
                            const statusResponse = await marketingService.checkStatus(
                                firstTariff.name,
                                firstLevel.level
                            );

                            if (statusResponse.success) {
                                if (statusResponse.data.status === 'payment_processing') {
                                    setIsActivated(false);
                                    setBasketStatus('payment_processing');

                                    // Используем сохраненную корзину для отображения
                                    if (lastBasketItems.length > 0) {
                                        setProcessingInfo({
                                            clicked_at: statusResponse.data.details.clicked_at,
                                            items: lastBasketItems.map(item => ({
                                                tarif: item.display_tarif,
                                                level: item.level
                                            }))
                                        });
                                    } else {
                                        // Если корзины нет, показываем хотя бы первый элемент
                                        setProcessingInfo({
                                            clicked_at: statusResponse.data.details.clicked_at,
                                            items: [{
                                                tarif: statusResponse.data.details.display_tarif,
                                                level: statusResponse.data.details.level
                                            }]
                                        });
                                    }

                                    setShowTree(false);
                                    setTreeParams(null);
                                    setQueueInfo(null);
                                } else if (statusResponse.data.status === 'in_queue') {
                                    setIsActivated(true);
                                    setBasketStatus('in_queue');
                                    setQueueInfo({
                                        tarif: firstTariff.name,
                                        displayTarif: statusResponse.data.details.display_tarif,
                                        level: firstLevel.level,
                                        queuedAt: statusResponse.data.details.queued_at,
                                        isTechnical: statusResponse.data.details.is_technical || false
                                    });
                                    setShowTree(false);
                                    setTreeParams(null);
                                } else {
                                    setIsActivated(false);
                                    setBasketStatus('basket');
                                    setProcessingInfo(null);
                                }
                            }
                        } catch (err) {
                            console.error('Error checking status:', err);
                            setIsActivated(false);
                            setBasketStatus('basket');
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error checking activation:', error);
        }
    };

    const loadOptions = async () => {
        setLoading(true);
        try {
            const response = await marketingService.getLightBoxOptions();
            if (response.success) {
                setAvailableTariffs(response.data.tariffs);

                if (response.data.tariffs.length > 0) {
                    const firstTariff = response.data.tariffs[0];
                    setActiveTariff(firstTariff.name);

                    if (firstTariff.levels.length > 0) {
                        setActiveLevel(firstTariff.levels[0]);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading options:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadBasket = async () => {
        try {
            const response = await marketingService.getBasket();
            if (response.success) {
                // Не трогаем состояние если уже идет обработка
                if (basketStatus === 'payment_processing' || basketStatus === 'in_queue') {
                    return;
                }

                setBasketItems(response.data.items || []);

                if (response.data.payment_available_at) {
                    setBasketTimer(response.data.payment_available_at);
                }
            }
        } catch (error) {
            console.error('Error loading basket:', error);
        }
    };

    const handleAddToBasket = async () => {
        if (!activeLevel) return;

        try {
            const response = await marketingService.addToBasket({
                tarif: activeTariff,
                level: activeLevel.level
            });

            if (response.success) {
                alert('Уровень добавлен в корзину!');
                loadBasket();
                setSelectedItem(null);
            } else {
                alert(response.message || 'Ошибка при добавлении в корзину');
            }
        } catch (error) {
            alert(error.message || 'Ошибка при добавлении в корзину');
        }
    };

    const handleRemoveFromBasket = async (itemId) => {
        try {
            const response = await marketingService.removeFromBasket(itemId);
            if (response.success) {
                loadBasket();
            }
        } catch (error) {
            console.error('Error removing from basket:', error);
        }
    };

    const handlePayBasket = async () => {
        if (basketItems.length === 0) {
            alert('Корзина пуста');
            return;
        }

        if (!canPay) {
            alert('Оплата будет доступна после окончания таймера');
            return;
        }

        if (isPaymentClicked) {
            return; // Предотвращаем повторные клики
        }

        setIsPaymentClicked(true);
        setLastBasketItems([...basketItems]); // Сохраняем корзину перед оплатой

        try {
            const response = await marketingService.payBasket();
            if (response.success) {
                setBasketItems([]);
                setHasCheckedStatus(false);
                checkActivationStatus();
            } else {
                alert(response.message || 'Ошибка при оплате');
                setIsPaymentClicked(false); // Разблокируем при ошибке
            }
        } catch (error) {
            alert(error.message || 'Ошибка при оплате');
            setIsPaymentClicked(false); // Разблокируем при ошибке
        }
    };

    const handleViewTree = async (tarif, level, numberLevel = 1) => {
        const params = new URLSearchParams();
        params.set('program', tarif);
        params.set('level', level);
        params.set('mentor', 1);

        setSearchParams(params);

        try {
            const response = await marketingService.checkStatus(tarif, level);

            if (response.success) {
                if (response.data.status === 'activated') {
                    setQueueInfo(null);
                    setShowTree(true);
                    setTreeParams({
                        tarif,
                        level,
                        numberLevel
                    });
                } else if (response.data.status === 'in_queue') {
                    setShowTree(false);
                    setTreeParams(null);
                    setQueueInfo({
                        tarif: tarif,
                        displayTarif: response.data.details.display_tarif,
                        level: level,
                        queuedAt: response.data.details.queued_at,
                        isTechnical: response.data.details.is_technical || false
                    });
                } else {
                    alert('Уровень не активирован. Добавьте его в корзину и оплатите.');
                }
            }
        } catch (error) {
            alert(error.message || 'Ошибка при проверке статуса');
        }
    };

    const handleBackFromTree = (params) => {
        if (params) {
            const urlParams = new URLSearchParams();
            urlParams.set('program', params.tarif);
            urlParams.set('level', params.level);
            urlParams.set('mentor', 1);

            setSearchParams(urlParams);

            setTreeParams({
                tarif: params.tarif,
                level: params.level,
                numberLevel: params.numberLevel || 1
            });
        } else {
            setSearchParams({});
            setShowTree(false);
            setTreeParams(null);
            setQueueInfo(null);
        }
    };

    const handleBack = () => {
        navigate('/dashboard/clusters');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Загрузка...</p>
                </div>
            </div>
        );
    }

    if (showTree && treeParams) {
        return (
            <TreeStructure
                tarif={treeParams.tarif}
                level={treeParams.level}
                numberLevel={treeParams.numberLevel}
                onBack={handleBackFromTree}
            />
        );
    }

    if (queueInfo) {
        return (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="border-b px-4 py-3">
                    <div className="flex items-center">
                        <button className="mr-3 text-gray-500 hover:text-blue-500 transition-colors" onClick={() => setQueueInfo(null)}>
                            <ChevronsLeft size={20} />
                        </button>
                        <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 mr-2">
                                <Box size={16} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">Light Box - В очереди</h3>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <div className="max-w-2xl mx-auto">
                        <div className="text-center mb-6">
                            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                                <Clock size={48} className="text-blue-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Ваше место в очереди</h2>
                            <p className="text-gray-600">
                                Ваш уровень <span className="font-semibold text-blue-600">{queueInfo.displayTarif}</span> (Уровень {queueInfo.level}) находится в очереди на расстановку.
                            </p>
                        </div>

                        <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl p-6 border border-blue-200">
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-700">Тариф:</span>
                                    <span className="font-semibold text-gray-900">{queueInfo.displayTarif}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-700">Уровень:</span>
                                    <span className="font-semibold text-gray-900">{queueInfo.level}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-700">Добавлено:</span>
                                    <span className="font-semibold text-gray-900">{queueInfo.queuedAt}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-700">Тип:</span>
                                    <span className="font-semibold text-gray-900">
                                        {queueInfo.isTechnical ? 'Технический' : 'Обычный'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <AlertCircle size={20} className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h4 className="font-semibold text-yellow-800 mb-1">Ожидайте размещения</h4>
                                    <p className="text-sm text-yellow-700">
                                        Обычно это занимает от нескольких минут до нескольких часов. Вы получите уведомление, когда место будет активировано.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isActivated) {
        return (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="border-b px-4 py-3">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <button className="mr-3 text-gray-500 hover:text-blue-500 transition-colors" onClick={handleBack}>
                                <ChevronsLeft size={20} />
                            </button>
                            <div className="flex items-center">
                                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 mr-2">
                                    <Box size={16} />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800">Light Box - Мои уровни</h3>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <h4 className="font-semibold text-gray-800 mb-2">Ваша активация</h4>
                        <p className="text-sm text-gray-600">
                            Тариф: <span className="font-medium text-blue-600">{activationDetails?.display_tarif}</span> |
                            Уровень: <span className="font-medium text-blue-600">{activationDetails?.level}</span> |
                            Активирован: <span className="font-medium text-blue-600">{activationDetails?.activated_at}</span>
                        </p>
                    </div>

                    <div className="space-y-4">
                        {availableTariffs.map((tariff) => (
                            <div key={tariff.name} className="border rounded-lg overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-50 to-white px-4 py-3 border-b">
                                    <h4 className="font-semibold text-gray-800">{tariff.display_name}</h4>
                                </div>

                                <div className="p-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {tariff.levels.map((level) => (
                                            <div
                                                key={level.level}
                                                className="border rounded-lg p-3 hover:shadow-md transition"
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <div className="text-xs text-gray-600 mb-1">Уровень</div>
                                                        <div className="text-lg font-bold text-gray-800">{level.number_level}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-xs text-gray-600 mb-1">Стоимость</div>
                                                        <div className="text-sm font-bold text-blue-500">{level.level} $</div>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => handleViewTree(tariff.name, level.level, 1)}
                                                    className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm font-medium"
                                                >
                                                    Смотреть структуру
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="border-b px-4 py-3">
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <button className="mr-3 text-gray-500 hover:text-blue-500 transition-colors" onClick={handleBack}>
                            <ChevronsLeft size={20} />
                        </button>
                        <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 mr-2">
                                <Box size={16} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">Light Box</h3>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-4 md:p-6">
                {/* Статус обработки платежа - СИНИЙ БЛОК */}
                {basketStatus === 'payment_processing' && processingInfo && (
                    <div className="mb-6 bg-blue-50 border-2 border-blue-300 rounded-2xl p-4 md:p-6 shadow-lg">
                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                            <div className="flex-shrink-0">
                                <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Clock className="w-6 h-6 md:w-8 md:h-8 text-blue-600 animate-pulse" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg md:text-xl font-bold text-blue-900 mb-3 flex items-center gap-2">
                                    <span>⏳</span>
                                    Ваш платеж обрабатывается
                                </h3>
                                <div className="space-y-2 mb-4">
                                    <p className="text-sm md:text-base text-blue-700">
                                        Кнопка оплаты нажата: <strong>{processingInfo.clicked_at}</strong>
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <p className="text-xs md:text-sm text-blue-700 font-semibold mb-2">Обрабатываемые позиции:</p>
                                    <div className="space-y-1">
                                        {processingInfo.items.map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-xs md:text-sm text-blue-600 bg-blue-100 rounded px-2 py-1">
                                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                <span>{item.tarif} - {item.level} $</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="bg-blue-100 rounded-lg p-3 border border-blue-200">
                                    <p className="text-xs text-blue-800">
                                        ℹ️ Ваш платеж проверяется системой.
                                        <strong className="block mt-1">⏱️ Страница обновляется каждые 10 секунд</strong>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ВЫБОР ТАРИФОВ - скрываем при payment_processing */}
                {basketStatus === 'basket' && (
                    <>
                        <div className="mb-6">
                            <h4 className="text-base md:text-lg font-bold text-gray-800 mb-3 md:mb-5">
                                Выберите уровень для добавления в корзину
                            </h4>

                            <div className="grid grid-cols-2 md:grid-cols-1 md:grid-flow-col md:auto-cols-fr gap-2 md:gap-4">
                                {availableTariffs.map((tariff) => {
                                    const firstLevel = tariff.levels.find(level => level.number_level === 1);

                                    if (!firstLevel) return null;

                                    const isSelected = activeLevel?.level === firstLevel.level && activeTariff === tariff.name;

                                    return (
                                        <div
                                            key={tariff.name}
                                            onClick={() => {
                                                setActiveLevel(firstLevel);
                                                setActiveTariff(tariff.name);
                                                setSelectedItem({
                                                    tariff: tariff.name,
                                                    level: firstLevel.level,
                                                    number_level: firstLevel.number_level,
                                                    awards: firstLevel.awards
                                                });
                                            }}
                                            className={`cursor-pointer border-2 rounded-xl md:rounded-2xl p-3 md:p-5 transition-all transform hover:scale-105 ${
                                                isSelected
                                                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-xl scale-105'
                                                    : 'border-gray-300 bg-white hover:border-blue-400 hover:shadow-lg'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2 md:mb-4">
                                                <div className="flex items-center gap-1.5 md:gap-2">
                                                    <div className={`w-7 h-7 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center ${
                                                        isSelected
                                                            ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                                                            : 'bg-gradient-to-br from-gray-400 to-gray-500'
                                                    }`}>
                                                        <Box size={14} className="text-white md:w-[18px] md:h-[18px]" />
                                                    </div>
                                                    <h5 className="text-xs md:text-sm font-bold text-gray-800 leading-tight">
                                                        {tariff.display_name}
                                                    </h5>
                                                </div>

                                                {isSelected && (
                                                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-blue-500 flex items-center justify-center animate-pulse">
                                                        <CheckCircle size={16} className="text-white md:w-[20px] md:h-[20px]" />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="text-center py-2 md:py-4 border-t border-b border-gray-200">
                                                <div className="text-[10px] md:text-xs text-gray-600 mb-1 md:mb-2 font-medium uppercase tracking-wide">
                                                    Первый уровень
                                                </div>
                                                <div className="text-2xl md:text-4xl font-bold text-gray-800 mb-1 md:mb-2">
                                                    {firstLevel.number_level}
                                                </div>
                                                <div className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3">Уровень</div>

                                                <div className={`inline-flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl font-bold text-base md:text-xl ${
                                                    isSelected
                                                        ? 'bg-blue-500 text-white'
                                                        : 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-600'
                                                }`}>
                                                    {firstLevel.level} $
                                                </div>
                                            </div>

                                            <div className="mt-2 md:mt-4">
                                                {isSelected ? (
                                                    <div className="text-center text-xs md:text-sm font-semibold text-blue-600 flex items-center justify-center gap-1 md:gap-2">
                                                        <CheckCircle size={14} className="md:w-4 md:h-4" />
                                                        Выбрано
                                                    </div>
                                                ) : (
                                                    <div className="text-center text-[10px] md:text-sm text-gray-500">
                                                        Нажмите для выбора
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ВЫБРАННЫЙ ТОВАР */}
                        {selectedItem && (
                            <div className="mb-6 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-300 rounded-2xl p-4 shadow-lg">
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div className="flex items-center gap-3 w-full sm:w-auto">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                                            <Box size={22} className="text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-gray-600 mb-1 font-medium">Выбрано для добавления:</p>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-base font-bold text-gray-800">
                                                    Уровень {selectedItem.number_level}
                                                </span>
                                                <span className="text-gray-400">•</span>
                                                <span className="text-lg font-bold text-blue-600">{selectedItem.level} $</span>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleAddToBasket}
                                        className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-bold text-sm flex items-center justify-center gap-2"
                                    >
                                        <ShoppingCart size={18} />
                                        Добавить в корзину
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* КОРЗИНА */}
                        {basketItems.length > 0 ? (
                            <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-xl">
                                <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 px-4 py-4 border-b-2 border-gray-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-bold text-gray-800 flex items-center gap-2 text-lg">
                                            <ShoppingCart size={22} className="text-green-600" />
                                            Корзина
                                            <span className="ml-1 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
                                                {basketItems.length}
                                            </span>
                                        </h4>
                                    </div>

                                    {!canPay && timeRemaining > 0 ? (
                                        <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl p-3 border-2 border-yellow-300">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-2 flex-1">
                                                    <Clock size={20} className="text-orange-600 animate-pulse flex-shrink-0" />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        Оплата через:
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-md">
                                                    <span className="font-bold text-lg text-orange-600 tabular-nums">
                                                        {formatTime(timeRemaining)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-3 border-2 border-green-300">
                                            <div className="flex items-center justify-center gap-2">
                                                <CheckCircle size={20} className="text-green-600" />
                                                <span className="text-sm font-bold text-green-700">
                                                    ✓ Готово к оплате
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="p-3 md:p-4 space-y-2 max-h-80 overflow-y-auto">
                                    {basketItems.map((item) => (
                                        <div
                                            key={item.id}
                                            className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-3 border-2 border-gray-200 hover:border-blue-300 transition-all"
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md flex-shrink-0">
                                                        <Box size={18} className="text-white" />
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-bold text-gray-800 text-sm md:text-base truncate">
                                                            {item.display_tarif}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-lg font-medium text-xs">
                                                                Ур. {item.level}
                                                            </span>
                                                            <span className="font-bold text-green-600 text-sm">
                                                                {item.level} $
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => handleRemoveFromBasket(item.id)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all hover:scale-110 flex-shrink-0"
                                                    title="Удалить"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-4 border-t-2 border-gray-200">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-gray-600 font-medium text-sm">Итого:</span>
                                            <span className="text-2xl md:text-3xl font-bold text-green-600">
                                                {basketItems.reduce((sum, item) => sum + item.level, 0)} $
                                            </span>
                                        </div>

                                        <button
                                            onClick={handlePayBasket}
                                            disabled={!canPay || isPaymentClicked}
                                            className={`w-full sm:w-auto px-6 py-3 rounded-xl transition-all font-bold shadow-lg text-sm md:text-base flex items-center justify-center gap-2 ${
                                                isPaymentClicked
                                                    ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white cursor-wait opacity-80'
                                                    : canPay
                                                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 hover:shadow-xl transform hover:scale-105'
                                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                            }`}
                                        >
                                            {isPaymentClicked ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                    Обработка...
                                                </>
                                            ) : canPay ? (
                                                <>💳 Оплатить</>
                                            ) : (
                                                <>⏳ Ожидание</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-8 md:p-12 text-center">
                                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                                    <ShoppingCart size={32} className="text-gray-400" />
                                </div>
                                <p className="text-base md:text-lg font-semibold text-gray-700 mb-2">Корзина пуста</p>
                                <p className="text-sm text-gray-500">Выберите тариф и добавьте уровень в корзину</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default LightBoxCluster;
